import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import './EbPage.css';

const pad = (n) => String(n).padStart(2, '0');
const toDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayStr = () => toDate(new Date());
const lastMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}`; };
const num = (n, dp = 0) => (n == null ? '—' : Number(n).toLocaleString('en-IN', { maximumFractionDigits: dp }));
const rupees = (n) => (n == null ? '—' : `₹${Math.round(n).toLocaleString('en-IN')}`);
const niceDate = (s) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');
const niceMonth = (m) => (m ? new Date(`${m}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }) : '');
const EMPTY_BILL = { bill_month: lastMonth(), bill_no: '', prev_reading: '', curr_reading: '', units: '', amount: '', due_date: '' };

export default function EbPage() {
  const [meters, setMeters] = useState([]);
  const [meterId, setMeterId] = useState(() => { try { return Number(localStorage.getItem('icecream_eb_meter')) || null; } catch (e) { return null; } });
  const [summary, setSummary] = useState(null);
  const [readings, setReadings] = useState([]);
  const [bills, setBills] = useState([]);
  const [rDate, setRDate] = useState(todayStr());
  const [rValue, setRValue] = useState('');
  const [bill, setBill] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [payDate, setPayDate] = useState(todayStr());
  const [payMode, setPayMode] = useState('upi');
  const [meterForm, setMeterForm] = useState(null);
  const [dailyMonth, setDailyMonth] = useState(() => todayStr().slice(0, 7));
  const [daily, setDaily] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadMeters = useCallback(() => api.get('/eb/meters').then((list) => {
    setMeters(list);
    setMeterId((cur) => (list.some((m) => m.id === cur) ? cur : list[0] ? list[0].id : null));
  }).catch((e) => setError(e.message)), []);

  const load = useCallback(() => {
    if (!meterId) return;
    const q = `meter_id=${meterId}`;
    Promise.all([api.get(`/eb/summary?${q}`), api.get(`/eb/readings?${q}`), api.get(`/eb/bills?${q}`)])
      .then(([s, r, b]) => { setSummary(s); setReadings(r); setBills(b); })
      .catch((e) => setError(e.message));
  }, [meterId]);

  useEffect(() => { loadMeters(); }, [loadMeters]);
  const loadDaily = useCallback(() => api.get(`/eb/daily?month=${dailyMonth}`).then(setDaily).catch((e) => setError(e.message)), [dailyMonth]);
  useEffect(() => { loadDaily(); }, [loadDaily]);
  useEffect(() => { load(); try { if (meterId) localStorage.setItem('icecream_eb_meter', String(meterId)); } catch (e) { /* ignore */ } }, [load, meterId]);

  const run = async (fn) => {
    setError(''); setBusy(true);
    try { await fn(); load(); loadDaily(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const meter = meters.find((m) => m.id === meterId) || null;
  const combined = summary && summary.combined;

  const saveReading = (force = false) => run(async () => {
    try {
      const saved = await api.post('/eb/readings', { meter_id: meterId, reading_date: rDate, reading: rValue, force });
      setRValue('');
      if (saved && saved.replaced) window.alert(`${meter.name}'s reading for ${niceDate(rDate)} was updated (one reading per day).`);
    } catch (e) {
      if (!force && /lower than|higher than/.test(e.message) && window.confirm(`${e.message}\n\nSave anyway?`)) {
        await api.post('/eb/readings', { meter_id: meterId, reading_date: rDate, reading: rValue, force: true });
        setRValue('');
      } else throw e;
    }
  });

  const deleteReading = (r) => {
    if (!window.confirm(`Delete the ${niceDate(r.reading_date)} reading (${r.reading})?`)) return;
    run(() => api.post(`/eb/readings/${r.id}/delete`, {}));
  };

  const setB = (k) => (e) => setBill((b) => ({ ...b, [k]: e.target.value }));
  const autoUnits = bill && bill.prev_reading !== '' && bill.curr_reading !== '' ? Number(bill.curr_reading) - Number(bill.prev_reading) : null;
  const saveBill = () => run(async () => {
    await api.post('/eb/bills', { ...bill, meter_id: meterId, units: bill.units !== '' ? bill.units : autoUnits });
    setBill(null);
  });
  const openBill = () => {
    const lb = summary && summary.last_bill;
    setBill({ ...EMPTY_BILL, prev_reading: lb && lb.curr_reading != null ? String(lb.curr_reading) : '' });
  };

  const pay = () => run(async () => { await api.post(`/eb/bills/${payFor.id}/pay`, { paid_on: payDate, mode: payMode }); setPayFor(null); });
  const unpay = (b) => { if (window.confirm('Undo this payment? The matching expense entry will be removed.')) run(() => api.post(`/eb/bills/${b.id}/unpay`, {})); };
  const delBill = (b) => { if (window.confirm(`Delete the ${niceMonth(b.bill_month)} bill?`)) run(() => api.post(`/eb/bills/${b.id}/delete`, {})); };

  const saveMeter = () => run(async () => {
    const body = { name: meterForm.name, service_no: meterForm.service_no, rate: meterForm.rate };
    if (meterForm.id) await api.post(`/eb/meters/${meterForm.id}`, body);
    else { const m = await api.post('/eb/meters', body); setMeterId(m.id); }
    setMeterForm(null);
    await loadMeters();
  });

  const lastReading = summary && summary.last_reading;
  const stale = lastReading && (new Date(todayStr()) - new Date(lastReading.reading_date)) / 86400000 >= 3;

  return (
    <div className="eb-root">
      <header className="eb-head">
        <div>
          <h1>Electricity (EB)</h1>
          <p>Each meter has its own readings and bills. Note every meter's reading at closing. Paying a bill adds it to Expenses.</p>
        </div>
        <button type="button" className="eb-btn eb-noprint" onClick={() => window.print()}>Print</button>
      </header>

      {error && <div className="eb-alert">{error}</div>}

      {combined && (
        <section className="eb-cards">
          <div className="eb-card eb-card-all"><span>Last day's EB, all meters</span><strong className="is-accent">{rupees(combined.last_cost)}</strong><em>(today − yesterday) × rate</em></div>
          <div className="eb-card eb-card-all"><span>EB cost this month so far</span><strong>{rupees(combined.month_cost)}</strong></div>
          <div className="eb-card eb-card-all"><span>Units this month, all meters</span><strong>{num(combined.month_units, 1)}</strong></div>
          <div className="eb-card eb-card-all"><span>Projected month bill</span><strong>{rupees(combined.estimated_bill)}</strong></div>
          <div className="eb-card eb-card-all"><span>Unpaid bills</span><strong className={combined.unpaid_count ? 'is-warn' : 'is-good'}>{combined.unpaid_count ? rupees(combined.unpaid_total) : 'None'}</strong>
            {combined.next_due && <em>Next due {niceDate(combined.next_due)}</em>}</div>
        </section>
      )}

      <div className="eb-meters eb-noprint" role="tablist">
        {meters.map((m) => (
          <button key={m.id} type="button" role="tab" aria-selected={m.id === meterId}
            className={`eb-meter-tab ${m.id === meterId ? 'is-on' : ''}`} onClick={() => setMeterId(m.id)}>
            <b>{m.name}</b><small>₹{num(m.rate, 2)}/unit{m.service_no ? ` · ${m.service_no}` : ''}</small>
          </button>
        ))}
        {meter && <button type="button" className="eb-link" onClick={() => setMeterForm({ id: meter.id, name: meter.name, service_no: meter.service_no || '', rate: String(meter.rate || 8.2) })}>Edit meter</button>}
        <button type="button" className="eb-link" onClick={() => setMeterForm({ name: '', service_no: '', rate: '8.2' })}>+ Add meter</button>
      </div>

      {meterForm && (
        <div className="eb-panel eb-noprint">
          <h2>{meterForm.id ? 'Edit meter' : 'New meter'}</h2>
          <div className="eb-grid">
            <label>Name<input value={meterForm.name} onChange={(e) => setMeterForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Shop, Kitchen" autoFocus /></label>
            <label>Service number (optional)<input value={meterForm.service_no} onChange={(e) => setMeterForm((f) => ({ ...f, service_no: e.target.value }))} placeholder="from the TNEB bill" /></label>
            <label>Rate per unit ₹<input type="number" inputMode="decimal" step="0.01" value={meterForm.rate} onChange={(e) => setMeterForm((f) => ({ ...f, rate: e.target.value }))} /></label>
          </div>
          <div className="eb-actions">
            <button type="button" className="eb-btn" onClick={() => setMeterForm(null)}>Cancel</button>
            <button type="button" className="eb-btn eb-btn-primary" disabled={busy || !meterForm.name.trim()} onClick={saveMeter}>Save</button>
          </div>
        </div>
      )}

      {summary && meter && (
        <>
          <h2 className="eb-meter-title">{meter.name}{meter.service_no ? ` · ${meter.service_no}` : ''}</h2>
          <section className="eb-cards">
            <div className="eb-card"><span>Last day</span><strong className="is-accent">{rupees(summary.last_cost)}</strong><em>{summary.last_units == null ? 'needs two readings' : `${num(summary.last_units, 1)} units × ₹${num(summary.rate, 2)}`}</em></div>
            <div className="eb-card"><span>Cost in {niceMonth(summary.month)} so far</span><strong>{rupees(summary.month_cost)}</strong><em>{num(summary.month_units, 1)} units</em></div>
            <div className="eb-card"><span>Average per day</span><strong>{num(summary.per_day, 1)}</strong><em>units</em></div>
            <div className="eb-card"><span>Projected month bill</span><strong>{rupees(summary.estimated_bill)}</strong><em>{num(summary.projected_units)} units</em></div>
          </section>
        </>
      )}
      {summary && meter && summary.month_units == null && (
        <p className="eb-hint">Add two readings for {meter.name} (yesterday's and today's) to see units and cost. Cost = (today − yesterday) × ₹{num(meter.rate, 2)}.</p>
      )}
      {stale && <p className="eb-hint is-warn">{meter.name}'s last reading was on {niceDate(lastReading.reading_date)}. Note today's reading to keep the numbers accurate.</p>}

      {meter && (
        <div className="eb-cols">
          <section className="eb-panel">
            <h2>Meter reading, {meter.name}</h2>
            <div className="eb-reading-form eb-noprint">
              <label>Date<input type="date" value={rDate} max={todayStr()} onChange={(e) => setRDate(e.target.value)} /></label>
              <label>Reading<input type="number" inputMode="decimal" placeholder={lastReading ? `last ${lastReading.reading}` : 'e.g. 45210'}
                value={rValue} onChange={(e) => setRValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && rValue !== '') saveReading(); }} /></label>
              <button type="button" className="eb-btn eb-btn-primary" disabled={busy || rValue === ''} onClick={() => saveReading()}>Save</button>
            </div>
            <div className="eb-table-wrap">
              <table className="eb-table">
                <thead><tr><th>Date</th><th className="num">Reading</th><th className="num">Units</th><th className="num">Cost</th><th className="eb-noprint" /></tr></thead>
                <tbody>
                  {readings.map((r) => (
                    <tr key={r.id}>
                      <td>{niceDate(r.reading_date)}</td>
                      <td className="num">{num(r.reading, 1)}</td>
                      <td className="num">{r.units == null ? '—' : num(r.units, 1)}{r.days > 1 ? <small> / {r.days} days</small> : null}</td>
                      <td className="num"><strong>{r.cost == null ? '—' : rupees(r.cost)}</strong></td>
                      <td className="eb-noprint"><button type="button" className="eb-link is-danger" onClick={() => deleteReading(r)}>Delete</button></td>
                    </tr>
                  ))}
                  {readings.length === 0 && <tr><td colSpan={5} className="eb-empty">No readings for {meter.name} yet. Note today's reading to start.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <div className="eb-right">
          <section className="eb-panel">
            <div className="eb-panel-head">
              <h2>Daily consumption, all meters</h2>
              <input type="month" className="eb-noprint" value={dailyMonth} max={todayStr().slice(0, 7)} onChange={(e) => e.target.value && setDailyMonth(e.target.value)} style={{ width: 170 }} />
            </div>
            {daily && (() => {
              const rates = [...new Set(daily.meters.map((m) => Number(m.rate)))];
              return (
                <div className="eb-table-wrap">
                  <table className="eb-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        {daily.meters.map((m) => <th key={m.id} className="num">{m.name} units</th>)}
                        <th className="num">Total units</th>
                        <th className="num">₹{rates.length === 1 ? ` (@ ₹${num(rates[0], 2)})` : ''}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {daily.rows.map((r) => (
                        <tr key={r.date}>
                          <td>{niceDate(r.date)}</td>
                          {daily.meters.map((m) => <td key={m.id} className="num">{r.units[m.id] == null ? '—' : num(r.units[m.id], 1)}</td>)}
                          <td className="num"><strong>{num(r.total_units, 1)}</strong></td>
                          <td className="num"><strong>{rupees(r.value)}</strong></td>
                        </tr>
                      ))}
                      {daily.rows.length === 0 && <tr><td colSpan={daily.meters.length + 3} className="eb-empty">No readings for this month yet. Enter each meter's reading every day.</td></tr>}
                    </tbody>
                    {daily.rows.length > 0 && (
                      <tfoot>
                        <tr className="eb-total-row">
                          <td>Total ({daily.rows.length} days)</td>
                          {daily.meters.map((m) => <td key={m.id} className="num">{num(daily.totals.units[m.id], 1)}</td>)}
                          <td className="num">{num(daily.totals.total_units, 1)}</td>
                          <td className="num">{rupees(daily.totals.value)}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              );
            })()}
          </section>

          <section className="eb-panel">
            <div className="eb-panel-head">
              <h2>Monthly bills, {meter.name}</h2>
              {!bill && <button type="button" className="eb-btn eb-btn-primary eb-noprint" onClick={openBill}>Add bill</button>}
            </div>
            {bill && (
              <div className="eb-bill-form eb-noprint">
                <div className="eb-grid">
                  <label>Bill month<input type="month" value={bill.bill_month} onChange={setB('bill_month')} /></label>
                  <label>Bill number<input value={bill.bill_no} onChange={setB('bill_no')} placeholder="optional" /></label>
                  <label>Previous reading<input type="number" inputMode="decimal" value={bill.prev_reading} onChange={setB('prev_reading')} /></label>
                  <label>Current reading<input type="number" inputMode="decimal" value={bill.curr_reading} onChange={setB('curr_reading')} /></label>
                  <label>Units<input type="number" inputMode="decimal" value={bill.units} onChange={setB('units')} placeholder={autoUnits != null ? `${autoUnits} (from readings)` : ''} /></label>
                  <label>Amount ₹<input type="number" inputMode="decimal" value={bill.amount} onChange={setB('amount')} /></label>
                  <label>Due date<input type="date" value={bill.due_date} onChange={setB('due_date')} /></label>
                </div>
                <div className="eb-actions">
                  <button type="button" className="eb-btn" onClick={() => setBill(null)}>Cancel</button>
                  <button type="button" className="eb-btn eb-btn-primary" disabled={busy || !bill.amount} onClick={saveBill}>Save bill</button>
                </div>
              </div>
            )}
            <div className="eb-table-wrap">
              <table className="eb-table">
                <thead><tr><th>Month</th><th className="num">Units</th><th className="num">Amount</th><th className="num">₹/unit</th><th>Status</th><th className="eb-noprint" /></tr></thead>
                <tbody>
                  {bills.map((b) => (
                    <tr key={b.id}>
                      <td>{niceMonth(b.bill_month)}{b.bill_no ? <small> #{b.bill_no}</small> : null}</td>
                      <td className="num">{num(b.units)}</td>
                      <td className="num">{rupees(b.amount)}</td>
                      <td className="num">{b.rate == null ? '—' : `₹${num(b.rate, 2)}`}</td>
                      <td>{b.paid_on ? <span className="eb-pill is-good">Paid {niceDate(b.paid_on)}</span> : <span className="eb-pill is-warn">{b.due_date ? `Due ${niceDate(b.due_date)}` : 'Unpaid'}</span>}</td>
                      <td className="eb-noprint eb-row-actions">
                        {b.paid_on
                          ? <button type="button" className="eb-link" onClick={() => unpay(b)}>Undo</button>
                          : <>
                              <button type="button" className="eb-link" onClick={() => { setPayFor(b); setPayDate(todayStr()); }}>Mark paid</button>
                              <button type="button" className="eb-link is-danger" onClick={() => delBill(b)}>Delete</button>
                            </>}
                      </td>
                    </tr>
                  ))}
                  {bills.length === 0 && <tr><td colSpan={6} className="eb-empty">No bills for {meter.name} yet. Add last month's bill to get the ₹ per unit.</td></tr>}
                </tbody>
              </table>
            </div>
            {payFor && (
              <div className="eb-pay eb-noprint">
                <strong>Pay {niceMonth(payFor.bill_month)}, {rupees(payFor.amount)}</strong>
                <input type="date" value={payDate} max={todayStr()} onChange={(e) => setPayDate(e.target.value)} />
                <select value={payMode} onChange={(e) => setPayMode(e.target.value)}>
                  <option value="upi">UPI</option><option value="cash">Cash</option><option value="card">Card</option><option value="bank">Bank</option>
                </select>
                <button type="button" className="eb-btn" onClick={() => setPayFor(null)}>Cancel</button>
                <button type="button" className="eb-btn eb-btn-primary" disabled={busy} onClick={pay}>Mark paid</button>
              </div>
            )}
          </section>
          </div>
        </div>
      )}
    </div>
  );
}
